import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { operatoriesService } from '../infrastructure/operatories.service';
import type {
  CreateOperatoryInput,
  UpdateOperatoryInput,
} from '../infrastructure/operatories.service';

export const operatoryKeys = {
  all: ['operatories'] as const,
  lists: () => [...operatoryKeys.all, 'list'] as const,
  list: (active?: boolean, type?: string) => [...operatoryKeys.lists(), { active, type }] as const,
  details: () => [...operatoryKeys.all, 'detail'] as const,
  detail: (id: string) => [...operatoryKeys.details(), id] as const,
};

export function useOperatoryList(active?: boolean, type?: string) {
  return useQuery({
    queryKey: operatoryKeys.list(active, type),
    queryFn: () => operatoriesService.list(active, type),
  });
}

export function useOperatory(id: string | undefined) {
  return useQuery({
    queryKey: operatoryKeys.detail(id!),
    queryFn: () => operatoriesService.getById(id!),
    enabled: !!id,
  });
}

export function useCreateOperatory() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateOperatoryInput) => operatoriesService.create(input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: operatoryKeys.lists() });
    },
  });
}

export function useUpdateOperatory() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, input }: { id: string; input: UpdateOperatoryInput }) =>
      operatoriesService.update(id, input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: operatoryKeys.lists() });
    },
  });
}

export function useDeactivateOperatory() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => operatoriesService.deactivate(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: operatoryKeys.lists() });
    },
  });
}
